// 01 — Where will your flock live? Two cards and one question. The hens chip
// carries the cap `sim/spots.ts` enforces for that housing in Normal, so the
// first thing a player reads is a number the game will actually hold them to
// (REFOCUS §5, §11a).

import { t } from '../../i18n';
import { chip } from '../components/chip';
import { button, div, h, link } from '../dom';
import { HOUSING_CARDS } from '../placeholders';
import type { HousingView } from '../selectors';
import { selectHousingCards } from '../selectors';
import type { Screen, ScreenContext } from '../router';
import type { HousingType } from '../../core/types';
// Forward-only, so there is no import cycle: every screen that can go back uses
// the router's history instead of naming its predecessor.
import { buyChicksScreen } from './buyChicks';

function housingCard(view: HousingView, selected: boolean, onPick: () => void): HTMLButtonElement {
  const card = button({
    class: `cf-housing${selected ? ' cf-housing--on' : ''}`,
    attrs: { 'aria-pressed': String(selected) },
    on: { click: onPick },
  });

  const art = div({ class: `cf-housing__art cf-housing__art--${view.housing}` });
  if (view.sprite) {
    art.appendChild(
      h('img', {
        class: 'cf-housing__img',
        attrs: { src: view.sprite, alt: t('home.coopArtAlt', { housing: t(view.nameKey) }) },
      }),
    );
  } else {
    art.appendChild(h('span', { class: 'cf-housing__noart', text: t('home.artNeeded') }));
  }

  card.append(
    art,
    div(
      { class: 'cf-housing__head' },
      h('span', { class: 'cf-housing__name', text: t(view.nameKey) }),
    ),
    div(
      { class: 'cf-housing__chips' },
      chip({ text: t('home.hens', { n: view.maxHens }) }),
      ...view.chips.map((key) => chip({ text: t(key) })),
    ),
  );
  return card;
}

export function homePickScreen(): Screen<HTMLElement> {
  return {
    id: 'homePick',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const views = selectHousingCards(HOUSING_CARDS);
      const chosen = (ctx.params.housing as HousingType) || 'backyard';
      const list = div({ class: 'cf-stack' });

      const screen = div(
        { class: 'cf-screen' },
        div(
          { class: 'cf-screen__head' },
          h('span', { class: 'cf-eyebrow', text: t('onboard.step', { n: '1', of: '5' }) }),
          h('span', { class: 'cf-brand', text: t('app.title') }),
        ),
        h('h1', { class: 'cf-display', text: t('home.title') }),
        h('p', { class: 'cf-sub', text: t('home.subtitle') }),
        list,
        div(
          { class: 'cf-screen__foot' },
          button({
            class: 'cf-btn cf-btn--primary',
            text: t('home.chooseChicks'),
            on: {
              click: () => ctx.router.push(buyChicksScreen(), { housing: chosen }),
            },
          }),
        ),
      );

      // Cards render into `list` after the heading so the primary action keeps
      // its place at the bottom of the screen whatever the card count is.
      for (const view of views) {
        list.appendChild(
          housingCard(view, view.housing === chosen, () => {
            ctx.router.replace(homePickScreen(), { housing: view.housing });
          }),
        );
      }
      // A screen with a primary action must still be able to leave the way a
      // kid expects: the back link returns to nothing on step 1, so it is the
      // hardware back button that does the leaving.
      if (ctx.router.canGoBack) {
        screen.querySelector('.cf-screen__head')?.prepend(
          link({
            class: 'cf-btn cf-btn--link',
            text: t('common.back'),
            on: { click: () => ctx.router.back() },
          }),
        );
      }
      screen.tabIndex = -1;
      return screen;
    },
  };
}
